import moment, { Moment } from 'moment-timezone';
import { z } from 'zod';

import { TimeWindow, timeWindowSchema } from '../../utils/datetimes/types';
import { removeUndefinedValues } from '../../utils/iteration';
import { AllowedQueryParams, useAxleQuery } from '../apiHelpers';
import { QhpSimple, qhpSimpleSchema } from '../sharedSchemas/basicSchemas';
import { clinicianAvailabilityDataSchema } from '../sharedSchemas/misc';
import { visitForListSchema } from '../sharedSchemas/visits';

const assigneeSchema = z.object({
    uuid: z.string().uuid(),
    name: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    logo_url: z.string().url().nullable(),
});
export type Assignee = z.infer<typeof assigneeSchema>;

export const jointAssignmentSchema = z.record(assigneeSchema);
export type JointAssignment = z.infer<typeof jointAssignmentSchema>;
export type JointAssignmentAndTime = {
    assignment: JointAssignment;
    time_option: TimeWindow;
};
const jointAssignmentAndTimesSchema = z.object({
    assignment: jointAssignmentSchema,
    time_options: z.array(timeWindowSchema),
});
export type JointAssignmentAndTimes = z.infer<typeof jointAssignmentAndTimesSchema>;

// Trimmed down availability schema, reducing bloat by using QHP IDs instead
//  of the full object where possible.
const trimAvailOverDateRangeSchema = z.object({
    qhp_objects: z.record(qhpSimpleSchema),
    assignable_qhps: z.array(z.string().uuid()),
    eligible_qhps_by_position_uuid: z.record(z.array(z.string().uuid())),
    confirmed_avail: z.array(
        z.object({
            assignment: z.record(z.string().uuid()),
            time_options: z.array(timeWindowSchema),
        }),
    ),
    unconfirmed_avail: z.array(timeWindowSchema),
});
type TrimAvailOverDateRange = z.infer<typeof trimAvailOverDateRangeSchema>;
const availOverDateRangeschema = z.object({
    assignable_qhps: z.array(qhpSimpleSchema),
    eligible_qhps_by_position_id: z.record(z.array(qhpSimpleSchema)),
    confirmed_avail: z.array(jointAssignmentAndTimesSchema),
    unconfirmed_avail: z.array(timeWindowSchema),
});
export type AvailOverDateRange = z.infer<typeof availOverDateRangeschema>;
const untrimAvailOverDateRange = (trim: TrimAvailOverDateRange): AvailOverDateRange => {
    return {
        assignable_qhps: trim.assignable_qhps.map((id) => trim.qhp_objects[id]),
        eligible_qhps_by_position_id: Object.fromEntries(
            Object.entries(trim.eligible_qhps_by_position_uuid).map(([positionId, qhpIds]) => [
                positionId,
                qhpIds.map((qhpId) => trim.qhp_objects[qhpId]),
            ]),
        ),
        confirmed_avail: trim.confirmed_avail.map((assignmentAndTimes) => ({
            assignment: Object.fromEntries(
                Object.entries(assignmentAndTimes.assignment).map(([positionId, qhpId]) => [
                    positionId,
                    trim.qhp_objects[qhpId],
                ]),
            ),
            time_options: assignmentAndTimes.time_options,
        })),
        unconfirmed_avail: trim.unconfirmed_avail,
    };
};

export const useVisitAvailOverDateRange = ({
    visitId,
    searchWindowStart,
    searchWindowEnd,
}: {
    visitId: string;
    searchWindowStart: Moment;
    searchWindowEnd: Moment;
}): AvailOverDateRange =>
    useAxleQuery({
        path: `/avail/visits/${visitId}/date-range`,
        params: {
            start_datetime: searchWindowStart.format(),
            end_datetime: searchWindowEnd.format(),
        },
        suspense: true,
        responseSchema: trimAvailOverDateRangeSchema,
        transform: untrimAvailOverDateRange,
    }).data;

const trimAvailAtProposedTimeSchema = z.object({
    qhp_objects: z.record(qhpSimpleSchema),
    assignable_qhps: z.array(z.string().uuid()),
    eligible_qhps_by_position_uuid: z.record(z.array(z.string().uuid())),
    available_qhps_by_position_uuid: z.record(z.array(z.string().uuid())).nullable(),
});
type TrimAvailAtProposedTime = z.infer<typeof trimAvailAtProposedTimeSchema>;
export type AvailAtProposedTime = {
    assignableQhps: QhpSimple[];
    eligibleQhpsByPositionId: Record<string, QhpSimple[]>;
    availableQhpsByPositionId: Record<string, QhpSimple[]> | null;
};
const untrimAvailAtProposedTime = (trim: TrimAvailAtProposedTime): AvailAtProposedTime => ({
    assignableQhps: trim.assignable_qhps.map((id) => trim.qhp_objects[id]),
    eligibleQhpsByPositionId: Object.fromEntries(
        Object.entries(trim.eligible_qhps_by_position_uuid).map(([positionId, qhpIds]) => [
            positionId,
            qhpIds.map((qhpId) => trim.qhp_objects[qhpId]),
        ]),
    ),
    availableQhpsByPositionId: trim.available_qhps_by_position_uuid
        ? Object.fromEntries(
              Object.entries(trim.available_qhps_by_position_uuid).map(([positionId, qhpIds]) => [
                  positionId,
                  qhpIds.map((qhpId) => trim.qhp_objects[qhpId]),
              ]),
          )
        : null,
});

const visitAvailabilityMetricsByDateSchema = z.object({
    availability_by_date: z.record(z.number()),
});

export type VisitAvailabilityMetricsByDate = {
    availabilityByDate: Record<string, number>;
};
export const useVisitAvailabilityMetricsByDate = ({
    enabled = true,
    startDate,
    endDate,
    patientId,
    addressId,
    serviceBundleId,
    serviceAreaId,
    serviceIds,
}: {
    enabled?: boolean;
    startDate: Moment;
    endDate: Moment;
    patientId: string;
    addressId: string;
    serviceAreaId: string;
    serviceBundleId: string;
    serviceIds: string[] | [];
}): VisitAvailabilityMetricsByDate | undefined =>
    useAxleQuery({
        path: `/avail/visits/metrics-by-date`,
        params: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
            patient_uuid: patientId,
            address_uuid: addressId,
            service_area_uuid: serviceAreaId,
            service_bundle_uuid: serviceBundleId,
            service_uuids: serviceIds,
        },
        suspense: false,
        responseSchema: visitAvailabilityMetricsByDateSchema,
        transform: (data) => ({
            availabilityByDate: data.availability_by_date,
        }),
        enabled,
    }).data;

export type CounterfactualPosition = {
    uuid: string;
    is_virtual: boolean;
    service_uuids: string[];
    start_offset_minutes: number | null;
    duration_override_minutes: number | null;
};

export type RequestCounterfactualPosition = {
    uuid: string;
    service_uuids: string[];
};

export const useVisitAvailAtProposedTime = ({
    visitId,
    newStartDatetime,
    newDurationOverrideMinutes,
    newPositions,
}: {
    visitId: string;
    newStartDatetime: Moment | null;
    newDurationOverrideMinutes?: number | null;
    newPositions?: CounterfactualPosition[];
}) =>
    useAxleQuery({
        path: `/avail/visits/${visitId}/proposed-time`,
        params: removeUndefinedValues({
            new_start_datetime: newStartDatetime?.format() ?? null,
            new_duration_override_minutes: newDurationOverrideMinutes,
            // TODO: Come up with way to avoid this stringification!
            new_positions: newPositions ? JSON.stringify(newPositions) : undefined,
        } as AllowedQueryParams) as AllowedQueryParams,
        suspense: false,
        responseSchema: trimAvailAtProposedTimeSchema,
        transform: untrimAvailAtProposedTime,
        keepPreviousData: true,
    }).data;

export const useVisitOrderAvailOverDateRange = ({
    visitOrderId,
    start,
    end,
}: {
    visitOrderId: string;
    start: Moment;
    end: Moment;
}) =>
    useAxleQuery({
        path: `/avail/visit-orders/${visitOrderId}/date-range`,
        params: {
            start_datetime: moment(start).format(),
            end_datetime: moment(end).format(),
        },
        suspense: true,
        responseSchema: trimAvailOverDateRangeSchema,
        transform: untrimAvailOverDateRange,
    }).data;

export const useVisitOrderAvailAtProposedTime = ({
    visitOrderId,
    startDatetime,
}: {
    visitOrderId: string;
    startDatetime: Moment | null;
}) =>
    useAxleQuery({
        path: `/avail/visit-orders/${visitOrderId}/proposed-time`,
        params: {
            new_start_datetime: startDatetime?.format() ?? '',
        },
        suspense: false,
        responseSchema: trimAvailAtProposedTimeSchema,
        transform: untrimAvailAtProposedTime,
    }).data;

// NOTE: The following schema is only valid for visits that have one position.
//  This will break when trying to apply to visits with multiple positions.
//  There is backend logic that will check whether a visit has multiple positions,
//  and it will not return any metrics if it does.
const unconfirmedAvailMetricsSchema = z.object({
    num_eligible_qhps: z.number(),
    num_booked_visits: z.number(),
});

export const useVisitOrderAvailOverDateRangeExtraData = ({
    visitOrderId,
    date,
    timezone,
}: {
    visitOrderId: string;
    date: Moment;
    timezone: string;
}) =>
    useAxleQuery({
        path: `/avail/visit-orders/${visitOrderId}/date-range/extra-data`,
        params: {
            visit_order_uuid: visitOrderId,
            date: date.tz(timezone).format('YYYY-MM-DD'),
        },
        suspense: true,
        responseSchema: z.object({
            supply_demand_metrics: unconfirmedAvailMetricsSchema.nullable(),
            visits_competing_for_attention: z.array(visitForListSchema).nullable(),
        }),
    }).data;

export const coordinatesSchema = z.object({
    lat: z.string(),
    lng: z.string(),
});

export const useCompetingVisitsOnDate = ({
    enabled = true,
    patientId,
    addressId,
    serviceAreaId,
    serviceBundleId,
    serviceIds,
    date,
    timezone,
}: {
    enabled?: boolean;
    patientId: string;
    addressId: string;
    serviceAreaId: string;
    serviceBundleId: string;
    serviceIds: string[];
    date: Moment;
    timezone: string;
}) =>
    useAxleQuery({
        path: `/avail/visits/competing-visits`,
        params: {
            patient_uuid: patientId,
            address_uuid: addressId,
            service_area_uuid: serviceAreaId,
            service_bundle_uuid: serviceBundleId,
            service_uuids: serviceIds,
            date: date.tz(timezone).format('YYYY-MM-DD'),
        },
        suspense: true,
        responseSchema: z.object({
            supply_demand_metrics: unconfirmedAvailMetricsSchema.nullable(),
            visits_competing_for_attention: z.array(visitForListSchema).nullable(),
            tentative_visit_coordinates: coordinatesSchema,
        }),
        enabled,
    }).data;

export const useClinicianAvailabilityMetricsByTimeslot = ({
    enabled = true,
    searchDate,
    patientId,
    addressId,
    serviceAreaId,
    serviceBundleId,
    serviceIds,
    intervalMinutes,
}: {
    enabled?: boolean;
    searchDate: Moment;
    patientId: string;
    addressId: string;
    serviceAreaId: string;
    serviceBundleId: string;
    serviceIds: string[];
    intervalMinutes: number;
}) =>
    useAxleQuery({
        path: `/avail/visits/metrics-by-timeslot`,
        params: {
            search_date: searchDate.format('YYYY-MM-DD'),
            patient_uuid: patientId,
            address_uuid: addressId,
            service_area_uuid: serviceAreaId,
            service_bundle_uuid: serviceBundleId,
            service_uuids: serviceIds,
            interval_minutes: intervalMinutes,
        },
        suspense: true,
        responseSchema: z.object({
            availability_by_timeslot: z.array(clinicianAvailabilityDataSchema),
        }),
        enabled,
    }).data.availability_by_timeslot;
